'use client';

import { useEffect, useRef, type RefObject } from 'react';
import { type CustomAnalyticsEvent, trackAnalyticsCustomEvent } from '../tracking';

/**
 * Calls trackAnalyticsCustomEvent when the element becomes visible for the first time.
 *
 * Example usage:
 *
 * ```tsx
 * const ref = useTrackFirstView({ eventCategory: 'Component Visible', eventAction: 'View' });
 *
 * return <div ref={ref} />;
 * ```
 */
/**
 * Calls `onFirstView` once, the first time the element becomes visible.
 *
 * Lower-level primitive behind {@link useTrackFirstView}. Reach for this when
 * first view should run custom logic — e.g. a list-impression event with its own
 * payload — rather than a standard CustomAnalyticsEvent. Each call site gets its
 * own observer, so independent impressions never share a debouncer and cancel
 * one another.
 */
export function useOnFirstView<T extends HTMLElement = HTMLElement>(
   onFirstView: () => void,
   { dwellMs = 0 }: { dwellMs?: number } = {}
): RefObject<T> {
   const ref = useRef<T | null>(null);
   const hasTrackedRef = useRef(false);
   const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

   // Keep the latest callback in a stable ref so inline functions don't cause
   // the observer setup to re-run on every render.
   const onFirstViewRef = useRef(onFirstView);
   useEffect(() => {
      onFirstViewRef.current = onFirstView;
   }, [onFirstView]);

   useEffect(() => {
      if (!ref.current || hasTrackedRef.current) return;

      const fire = () => {
         hasTrackedRef.current = true;
         onFirstViewRef.current();
         observer.disconnect();
      };

      const observer = new IntersectionObserver(
         entries => {
            for (const entry of entries) {
               if (!entry.isIntersecting) {
                  if (timerRef.current !== undefined) {
                     clearTimeout(timerRef.current);
                     timerRef.current = undefined;
                  }
                  continue;
               }
               if (dwellMs > 0) {
                  timerRef.current ??= setTimeout(fire, dwellMs);
               } else {
                  fire();
               }
               break;
            }
         },
         { threshold: 0 } // 0 means as soon as one pixel is visible
      );

      observer.observe(ref.current);
      return () => {
         if (timerRef.current !== undefined) {
            clearTimeout(timerRef.current);
            timerRef.current = undefined;
         }
         observer.disconnect();
      };
   }, [dwellMs]);

   return ref as RefObject<T>;
}

/**
 * Calls trackAnalyticsCustomEvent when the element becomes visible for the first time.
 *
 * Example usage:
 *
 * ```tsx
 * const ref = useTrackFirstView({ eventCategory: 'Component Visible', eventAction: 'View' });
 *
 * return <div ref={ref} />;
 * ```
 */
export function useTrackFirstView<T extends HTMLElement = HTMLElement>(
   eventData: CustomAnalyticsEvent | (() => CustomAnalyticsEvent),
   { dwellMs = 0 }: { dwellMs?: number } = {}
): RefObject<T> {
   return useOnFirstView<T>(
      () => {
         trackAnalyticsCustomEvent({
            ...(typeof eventData === 'function' ? eventData() : eventData),
         });
      },
      { dwellMs }
   );
}
